import React from "react";
import { Link } from "react-router-dom";
import {
  Sparkle,
  ShieldCheck,
  Database,
  Bell,
  Lightning,
  Users,
  HandHeart,
  Buildings,
  Tree,
  ClipboardText,
  MapTrifold,
  ChartLineUp,
  CheckCircle,
  Target,
  Newspaper,
  ListChecks,
  CloudArrowUp,
  LockKey,
  Briefcase,
  Wrench,
  Crosshair,
  Mountains,
  GraduationCap,
  MapPin,
  Calendar,
  Receipt,
  Stack,
  CurrencyDollar,
  CaretRight,
  Globe,
  ArrowRight,
} from "@phosphor-icons/react";

// =================================================================
// Propuesta Técnica + Económica · FCAB
// Each "page" is A4 portrait: 850×1200
// =================================================================

const PAGE_W = 794;  // A4 portrait at 96 DPI
const PAGE_H = 1123;

function Page({ id, children, bg = "default" }) {
  const bgClass = {
    default: "bg-white text-slate-900",
    dark: "bg-gradient-to-br from-slate-950 via-[#1a0f08] to-slate-950 text-white",
    orange: "bg-gradient-to-br from-brand-orange-600 via-brand-orange-500 to-amber-600 text-white",
  }[bg];
  return (
    <div
      id={id}
      className={`relative overflow-hidden shadow-2xl ${bgClass}`}
      style={{ width: PAGE_W, height: PAGE_H }}
    >
      {children}
    </div>
  );
}

function PageFrame({ id, title, num, total, bg = "default", children, accent = "orange" }) {
  const textColor = bg === "default" ? "text-slate-900" : "text-white";
  const subtle = bg === "default" ? "text-slate-500" : "text-white/60";
  const borderCol = bg === "default" ? "border-slate-200" : "border-white/10";
  return (
    <Page id={id} bg={bg}>
      {/* Top bar */}
      <div className={`absolute top-0 left-0 right-0 h-[72px] px-12 flex items-center justify-between z-20 border-b ${borderCol}`}>
        <div className="flex items-center gap-3">
          <div className={`w-1.5 h-8 rounded-full ${accent === "orange" ? "bg-brand-orange-500" : "bg-brand-green-500"}`} />
          <div>
            <div className={`text-[9px] uppercase tracking-[0.3em] font-bold ${accent === "orange" ? "text-brand-orange-600" : "text-brand-green-600"} ${bg !== "default" ? "!text-brand-orange-300" : ""}`}>Software · Gestión Social y Ambiental</div>
            <div className={`font-display text-base font-bold ${textColor}`}>SIGSAOP</div>
          </div>
        </div>
        <div className={`text-right ${subtle}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold">{title}</div>
          {num && total && <div className="text-[10px] mt-0.5">{num} / {total}</div>}
        </div>
      </div>
      {/* Bottom bar */}
      <div className={`absolute bottom-0 left-0 right-0 h-[60px] px-12 flex items-center justify-between z-20 border-t ${borderCol}`}>
        <div className={`text-[10px] uppercase tracking-[0.25em] font-semibold ${subtle}`}>
          Propuesta FCAB · Junio 2026
        </div>
        <div className={`text-[10px] uppercase tracking-[0.25em] font-semibold ${subtle}`}>
          sigsaop.cl
        </div>
      </div>
      {/* Content */}
      <div className="absolute inset-0 pt-[88px] pb-[76px] px-12">{children}</div>
    </Page>
  );
}

function SectionLabel({ children, accent = "orange" }) {
  const tones = {
    orange: "border-brand-orange-500/40 bg-brand-orange-500/10 text-brand-orange-600",
    green: "border-brand-green-500/40 bg-brand-green-500/10 text-brand-green-600",
    white: "border-white/40 bg-white/15 text-white",
  };
  return (
    <span className={`inline-flex items-center gap-2 rounded-full border ${tones[accent]} px-3 py-1 text-[10px] uppercase tracking-[0.25em] font-bold`}>
      <Sparkle size={10} weight="fill" /> {children}
    </span>
  );
}

// =================================================================
// 01 — Portada
// =================================================================
function PageCover() {
  return (
    <Page id="cover" bg="dark">
      <div className="absolute -top-32 -right-20 w-[500px] h-[500px] rounded-full bg-brand-orange-500/30 blur-3xl" />
      <div className="absolute -bottom-40 -left-32 w-[600px] h-[600px] rounded-full bg-brand-green-500/15 blur-3xl" />

      <div className="relative h-full flex flex-col justify-between px-14 py-14">
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-2 h-12 rounded-full bg-brand-orange-500" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-orange-300">Software · Gestión Social y Ambiental</div>
              <div className="font-display text-2xl font-bold tracking-wide">SIGSAOP</div>
            </div>
          </div>
          <SectionLabel accent="white">Propuesta confidencial · Junio 2026</SectionLabel>
          <h1 className="font-display text-[72px] leading-[0.95] font-bold mt-8 tracking-tight">
            Propuesta<br />Técnica y<br /><span className="text-brand-orange-400">Económica</span>.
          </h1>
        </div>

        <div>
          <div className="rounded-2xl border-2 border-brand-orange-400/40 bg-brand-orange-500/10 p-7 backdrop-blur-sm">
            <div className="text-[11px] uppercase tracking-[0.25em] text-brand-orange-300 font-bold">Preparada para</div>
            <div className="font-display text-4xl font-bold mt-2">FCAB</div>
            <div className="text-[15px] text-slate-300 mt-1">Ferrocarril de Antofagasta a Bolivia</div>
            <div className="mt-4 pt-4 border-t border-white/15">
              <div className="text-[10px] uppercase tracking-[0.25em] text-brand-orange-300 font-bold">Atención de</div>
              <div className="font-display text-xl font-bold mt-1">Álvaro Yáñez</div>
              <div className="text-[13px] text-slate-300">Gerente de Asuntos Públicos y Medioambiente</div>
            </div>
            <div className="mt-5 grid grid-cols-3 gap-3">
              <div className="rounded-lg bg-white/10 px-3 py-2.5">
                <div className="text-[9px] uppercase tracking-wider text-white/60">Implementación</div>
                <div className="font-display text-xl font-bold mt-1">14 días</div>
              </div>
              <div className="rounded-lg bg-white/10 px-3 py-2.5">
                <div className="text-[9px] uppercase tracking-wider text-white/60">Modalidad</div>
                <div className="font-display text-xl font-bold mt-1">SaaS</div>
              </div>
              <div className="rounded-lg bg-white/10 px-3 py-2.5">
                <div className="text-[9px] uppercase tracking-wider text-white/60">Origen</div>
                <div className="font-display text-xl font-bold mt-1">Antofagasta</div>
              </div>
            </div>
          </div>
          <div className="mt-6 flex items-center justify-between">
            <img
              src="/assets/sigsaop-logo-light.svg"
              alt="SIGSAOP"
              className="h-20 w-auto"
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">Documento</div>
              <div className="font-display text-base font-bold">PPT + PPE</div>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}

// =================================================================
// 02 — Resumen Ejecutivo
// =================================================================
function PageResumen() {
  return (
    <PageFrame id="resumen" title="Resumen Ejecutivo" num="02" total="11">
      <SectionLabel>Resumen Ejecutivo</SectionLabel>
      <h2 className="font-display text-[44px] leading-[1.05] font-bold mt-4 text-slate-900 max-w-[700px]">
        SIGSAOP: software de gestión social y ambiental, hecho en Antofagasta.
      </h2>
      <p className="text-[15px] text-slate-600 mt-5 leading-relaxed max-w-[700px]">
        Reemplazamos las planillas Excel y los Power Point por una plataforma SaaS
        que centraliza comunidades, autoridades, parlamentarios, proyectos, compromisos, RCA,
        asuntos públicos y agenda institucional — con trazabilidad, alertas automáticas y
        reportes ejecutivos en un click.
      </p>

      <div className="grid grid-cols-3 gap-4 mt-8">
        {[
          { icon: Lightning, t: "14 días", d: "Operativa en 2 semanas, con migración de datos y capacitación." },
          { icon: Stack, t: "10 módulos", d: "Comunidades, RCA, compromisos, proyectos, mapas y más." },
          { icon: ShieldCheck, t: "AWS · Ley 21.719", d: "Cifrado y alineado con la nueva ley de protección de datos de Chile." },
        ].map((c) => (
          <div key={c.t} className="rounded-xl border border-slate-200 bg-slate-50 p-5">
            <div className="w-11 h-11 rounded-lg bg-brand-orange-500/15 border border-brand-orange-500/30 flex items-center justify-center mb-3">
              <c.icon size={22} weight="duotone" className="text-brand-orange-600" />
            </div>
            <div className="font-display text-xl font-bold text-slate-900">{c.t}</div>
            <div className="text-[12px] text-slate-600 mt-1.5 leading-relaxed">{c.d}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border-2 border-brand-orange-500/30 bg-gradient-to-br from-brand-orange-500/10 to-transparent p-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-brand-orange-600 font-bold">Inversión propuesta</div>
        <div className="grid grid-cols-2 gap-5 mt-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Plan O&S</div>
            <div className="font-display text-3xl font-bold text-slate-900 mt-1">$1.500.000</div>
            <div className="text-[12px] text-slate-600 mt-1">mensual · no incluye IVA</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Plan Copilot</div>
            <div className="font-display text-3xl font-bold text-slate-900 mt-1">$2.300.000</div>
            <div className="text-[12px] text-slate-600 mt-1">mensual · incluye onboarding ejecutiva</div>
          </div>
        </div>
      </div>

      <p className="text-[13px] text-slate-500 italic mt-6 max-w-[700px]">
        Esta propuesta consolida la <strong>Propuesta Técnica (PPT)</strong> y la
        <strong> Propuesta Económica (PPE)</strong> en un único documento, con vigencia de 30 días desde su emisión.
      </p>
      <p className="text-[12px] text-slate-500 mt-3 max-w-[700px]">
        <strong>* O&amp;S</strong> = Operación y Soporte.
      </p>
    </PageFrame>
  );
}

// =================================================================
// 03 — Problema
// =================================================================
function PageProblema() {
  return (
    <PageFrame id="problema" title="El Problema" num="03" total="11">
      <SectionLabel>El problema a resolver</SectionLabel>
      <h2 className="font-display text-[44px] leading-[1.05] font-bold mt-4 text-slate-900 max-w-[700px]">
        Excel y Power Point no alcanzan para la gestión social y ambiental moderna.
      </h2>
      <p className="text-[15px] text-slate-600 mt-5 leading-relaxed max-w-[700px]">
        Una operación como FCAB convive con decenas de comunidades, autoridades regionales,
        compromisos derivados de RCA y proyectos de inversión social. Gestionar esa red
        crítica con planillas implica riesgos operacionales, legales y reputacionales.
      </p>

      <div className="grid grid-cols-2 gap-5 mt-8">
        {[
          { t: "Versiones duplicadas", d: "Nadie sabe cuál es la planilla vigente. La memoria institucional vive en PCs personales." },
          { t: "Compromisos vencidos sin aviso", d: "Plazos derivados de RCA o acuerdos con comunidades se cumplen tarde — o no se cumplen." },
          { t: "Cero trazabilidad ante auditoría", d: "Sin evidencia con autor y fecha, las observaciones se transforman en multas." },
          { t: "Reportes hechos a mano", d: "El equipo invierte horas armando PPT que quedan obsoletos en días." },
        ].map((c) => (
          <div key={c.t} className="rounded-xl border border-rose-200 bg-rose-50/50 p-5">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-rose-100 border border-rose-200 flex items-center justify-center flex-shrink-0">
                <Target size={18} weight="duotone" className="text-rose-600" />
              </div>
              <div>
                <div className="font-display text-lg font-bold text-slate-900">{c.t}</div>
                <div className="text-[13px] text-slate-600 mt-1 leading-relaxed">{c.d}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="absolute bottom-[90px] left-12 right-12">
        <div className="rounded-xl border-l-4 border-brand-orange-500 bg-brand-orange-50 px-5 py-4">
          <p className="text-[13px] text-slate-700 leading-relaxed">
            <strong className="text-brand-orange-700">Costo oculto:</strong> en gestión social y ambiental,
            lo que no se registra no se cumple — y lo que no se cumple, se cobra en multas, paralizaciones
            de RCA, denuncias comunitarias y daño reputacional.
          </p>
        </div>
      </div>
    </PageFrame>
  );
}

// =================================================================
// 04 — Metodología
// =================================================================
function PageMetodologia() {
  const steps = [
    { n: "1", t: "Diagnóstico", d: "Reunión con tu equipo para mapear procesos actuales, ejes estratégicos y bases de datos existentes.", icon: ClipboardText, dur: "Día 1-2" },
    { n: "2", t: "Configuración", d: "Personalizamos campos, módulos, permisos y ejes estratégicos a la operación de FCAB.", icon: Wrench, dur: "Día 3-5" },
    { n: "3", t: "Migración de datos", d: "Cargamos comunidades, autoridades, compromisos y proyectos desde tus Excels actuales.", icon: CloudArrowUp, dur: "Día 6-9" },
    { n: "4", t: "Capacitación", d: "2 sesiones de entrenamiento al equipo (operativo y ejecutivo) + manuales en línea.", icon: GraduationCap, dur: "Día 10-12" },
    { n: "5", t: "Go-live + acompañamiento", d: "Salida en producción, soporte intensivo y validación final del usuario designado.", icon: Lightning, dur: "Día 13-14" },
  ];
  return (
    <PageFrame id="metodologia" title="Metodología" num="04" total="11">
      <SectionLabel>Metodología de trabajo</SectionLabel>
      <h2 className="font-display text-[44px] leading-[1.05] font-bold mt-4 text-slate-900 max-w-[700px]">
        Cinco pasos, en <span className="text-brand-orange-600">14 días</span>.
      </h2>
      <p className="text-[15px] text-slate-600 mt-5 leading-relaxed max-w-[700px]">
        Un proceso llave en mano. Tu equipo opera SIGSAOP en producción la tercera semana.
      </p>

      <div className="mt-8 space-y-3">
        {steps.map((s, i) => (
          <div key={s.n} className="flex items-stretch gap-4">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-brand-orange-500 text-white font-display font-bold flex items-center justify-center text-lg shadow-md flex-shrink-0">{s.n}</div>
              {i < steps.length - 1 && <div className="w-px flex-1 bg-brand-orange-300 mt-1" />}
            </div>
            <div className="flex-1 rounded-xl border border-slate-200 bg-white px-5 py-4 flex items-start gap-4">
              <div className="w-11 h-11 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
                <s.icon size={22} weight="duotone" className="text-brand-orange-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div className="font-display text-lg font-bold text-slate-900">{s.t}</div>
                  <div className="text-[11px] uppercase tracking-wider text-brand-orange-600 font-bold">{s.dur}</div>
                </div>
                <div className="text-[13px] text-slate-600 mt-1 leading-relaxed">{s.d}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </PageFrame>
  );
}

// =================================================================
// 05 — Entregables
// =================================================================
function PageEntregables() {
  const items = [
    { icon: Database, t: "Plataforma SIGSAOP operativa", d: "Plataforma FCAB configurada, accesos para todo el equipo." },
    { icon: HandHeart, t: "Catastro de comunidades *", d: "Comunidades georreferenciadas con ficha completa." },
    { icon: Buildings, t: "Directorio de autoridades y GORE *", d: "Autoridades regionales, parlamentarios y entidades vinculadas." },
    { icon: ListChecks, t: "Compromisos cargados *", d: "Compromisos vigentes con responsable, plazo y evidencia." },
    { icon: Briefcase, t: "Proyectos con eje, inversión y ejecución *", d: "Iniciativas sociales y ambientales con KPI y beneficiarios." },
    { icon: Tree, t: "Módulo RCA configurado * (opcional)", d: "Resoluciones de calificación ambiental con sus compromisos asociados." },
    { icon: MapTrifold, t: "Mapa territorial activo", d: "Geolocalización de comunidades, autoridades y organizaciones." },
    { icon: ChartLineUp, t: "Dashboard ejecutivo", d: "Vista consolidada para directorio: KPIs, gráficos, exportables PDF/Excel." },
    { icon: Bell, t: "Alertas configuradas", d: "Email para responsables, en los plazos que defina FCAB." },
    { icon: GraduationCap, t: "Capacitación + manuales", d: "2 sesiones presenciales/remotas + biblioteca de tutoriales." },
  ];
  return (
    <PageFrame id="entregables" title="Entregables" num="05" total="11">
      <SectionLabel>Resultados esperados</SectionLabel>
      <h2 className="font-display text-[44px] leading-[1.05] font-bold mt-4 text-slate-900 max-w-[700px]">
        10 entregables concretos al cierre del día 14.
      </h2>
      <p className="text-[15px] text-slate-600 mt-5 leading-relaxed max-w-[700px]">
        Cada entregable es verificable, con un responsable asignado y aprobado por el usuario designado por FCAB.
      </p>

      <div className="grid grid-cols-2 gap-3 mt-7">
        {items.map((it, i) => (
          <div key={it.t} className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3.5 flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-brand-green-500/15 border border-brand-green-500/30 flex items-center justify-center flex-shrink-0">
              <it.icon size={18} weight="duotone" className="text-brand-green-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] text-slate-400 font-bold">{String(i + 1).padStart(2, "0")}</span>
                <div className="font-display text-[14px] font-bold text-slate-900 leading-tight">{it.t}</div>
              </div>
              <div className="text-[11px] text-slate-600 mt-0.5 leading-snug">{it.d}</div>
            </div>
          </div>
        ))}
      </div>

      <p className="text-[12px] text-slate-600 mt-5">
        <strong>(*)</strong> Servicio asociado al plan Copilot.
      </p>
    </PageFrame>
  );
}

// =================================================================
// 06 — Valor diferencial
// =================================================================
function PageValor() {
  return (
    <PageFrame id="valor" title="Valor diferencial" num="06" total="11">
      <SectionLabel accent="green">Por qué SIGSAOP</SectionLabel>
      <h2 className="font-display text-[44px] leading-[1.05] font-bold mt-4 text-slate-900 max-w-[700px]">
        Locales, expertos, <span className="text-brand-green-600">dueños del código</span>.
      </h2>
      <p className="text-[15px] text-slate-600 mt-5 leading-relaxed max-w-[700px]">
        No somos un integrador genérico. SIGSAOP es producto propio, diseñado para la realidad
        socioambiental del norte de Chile y operado desde Antofagasta.
      </p>

      <div className="grid grid-cols-2 gap-4 mt-8">
        {[
          { icon: MapPin, t: "Somos de Antofagasta", d: "Cercanía real, soporte en zona horaria local, conocimiento del territorio." },
          { icon: Wrench, t: "Desarrolladores del software", d: "Si necesitas un cambio, lo construimos. No dependemos de un proveedor externo." },
          { icon: Mountains, t: "Conocemos el problema", d: "Hemos vivido los mismos desafíos que tú, creando una solución que los resuelve de raíz." },
          { icon: ShieldCheck, t: "Cumplimiento legal", d: "Alineado con la nueva Ley 21.719 de protección de datos personales de Chile." },
        ].map((v) => (
          <div key={v.t} className="rounded-xl border-2 border-brand-green-500/30 bg-gradient-to-br from-brand-green-50 to-white p-5">
            <div className="w-12 h-12 rounded-lg bg-brand-green-500/15 border border-brand-green-500/30 flex items-center justify-center mb-3">
              <v.icon size={24} weight="duotone" className="text-brand-green-700" />
            </div>
            <div className="font-display text-xl font-bold text-slate-900">{v.t}</div>
            <div className="text-[13px] text-slate-600 mt-2 leading-relaxed">{v.d}</div>
          </div>
        ))}
      </div>

      <div className="absolute bottom-[90px] left-12 right-12">
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 flex items-center gap-4">
          <Globe size={28} weight="duotone" className="text-brand-orange-600 flex-shrink-0" />
          <p className="text-[13px] text-slate-700 leading-relaxed">
            <strong className="text-slate-900">De Antofagasta al mundo.</strong>
            {" "}Arquitectura lista para escalar a cualquier operación, en cualquier país.
          </p>
        </div>
      </div>
    </PageFrame>
  );
}

// =================================================================
// 07 — Cronograma
// =================================================================
function PageCronograma() {
  const phases = [
    { t: "Diagnóstico", start: 0, end: 2, color: "bg-brand-orange-500" },
    { t: "Configuración", start: 2, end: 5, color: "bg-brand-green-500" },
    { t: "Migración de datos", start: 5, end: 9, color: "bg-sky-500" },
    { t: "Capacitación", start: 9, end: 12, color: "bg-violet-500" },
    { t: "Go-live + soporte", start: 12, end: 14, color: "bg-rose-500" },
  ];
  return (
    <PageFrame id="cronograma" title="Cronograma" num="07" total="11">
      <SectionLabel>Cronograma de ejecución</SectionLabel>
      <h2 className="font-display text-[44px] leading-[1.05] font-bold mt-4 text-slate-900 max-w-[700px]">
        Plan de implementación · <span className="text-brand-orange-600">14 días</span>.
      </h2>
      <p className="text-[15px] text-slate-600 mt-5 leading-relaxed max-w-[700px]">
        Cronograma referencial — ajustable según disponibilidad del equipo FCAB y volumen de datos a migrar.
      </p>

      {/* Gantt */}
      <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5">
        {/* day numbers header */}
        <div className="flex items-center gap-1 ml-[160px] mb-2">
          {Array.from({ length: 14 }).map((_, i) => (
            <div key={i} className="flex-1 text-center text-[10px] font-mono text-slate-400 font-semibold">{i + 1}</div>
          ))}
        </div>
        <div className="space-y-2.5">
          {phases.map((p) => (
            <div key={p.t} className="flex items-center gap-3">
              <div className="w-[150px] text-[13px] font-semibold text-slate-700 truncate">{p.t}</div>
              <div className="flex-1 relative h-7 bg-slate-100 rounded-md overflow-hidden">
                <div
                  className={`absolute top-0 bottom-0 ${p.color} rounded-md flex items-center px-2.5 text-[10px] font-bold text-white`}
                  style={{
                    left: `${(p.start / 14) * 100}%`,
                    width: `${((p.end - p.start) / 14) * 100}%`,
                  }}
                >
                  D{p.start + 1}–D{p.end}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <Calendar size={20} weight="duotone" className="text-brand-orange-600 mb-2" />
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Inicio</div>
          <div className="font-display text-base font-bold text-slate-900 mt-0.5">Día 1 · Diagnóstico</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <Lightning size={20} weight="duotone" className="text-brand-orange-600 mb-2" />
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Go-live</div>
          <div className="font-display text-base font-bold text-slate-900 mt-0.5">Día 13</div>
        </div>
        <div className="rounded-xl border-2 border-brand-green-500/40 bg-brand-green-50 px-4 py-3">
          <CheckCircle size={20} weight="duotone" className="text-brand-green-700 mb-2" />
          <div className="text-[11px] uppercase tracking-wider text-brand-green-700 font-semibold">Cierre</div>
          <div className="font-display text-base font-bold text-slate-900 mt-0.5">Día 14 · Validación</div>
        </div>
      </div>
    </PageFrame>
  );
}

// =================================================================
// 08 — Plan O&S (PPE 1)
// =================================================================
function PagePlanOS() {
  return (
    <PageFrame id="plan-os" title="Plan O&S · Propuesta Económica" num="08" total="11">
      <SectionLabel>Plan O&S · Operación y Soporte</SectionLabel>
      <h2 className="font-display text-[44px] leading-[1.05] font-bold mt-4 text-slate-900 max-w-[700px]">
        Plan <span className="text-brand-orange-600">O&S</span>.
      </h2>
      <p className="text-[15px] text-slate-600 mt-4 leading-relaxed max-w-[700px]">
        SIGSAOP llave en mano. FCAB carga la información operacional con su propio equipo,
        nosotros mantenemos la plataforma operativa 24/7.
      </p>

      {/* Pricing card */}
      <div className="mt-6 rounded-2xl border-2 border-brand-orange-500/40 bg-gradient-to-br from-brand-orange-50 to-white p-6 shadow-lg">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-orange-600 font-bold">Inversión mensual</div>
            <div className="font-display text-[56px] font-bold text-slate-900 leading-none mt-2">
              $1.500.000
            </div>
            <div className="text-[12px] text-slate-500 mt-2">CLP mensual · no incluye IVA</div>
          </div>
          <div className="text-right">
            <Receipt size={56} weight="duotone" className="text-brand-orange-500" />
          </div>
        </div>

        <div className="mt-6 space-y-2.5">
          {[
            { t: "Diseño y personalización", v: "$0", note: "Sin costo · incluido" },
            { t: "Operación y Soporte Técnico 24/7", v: "$1.500.000", note: "Mensual" },
          ].map((r) => (
            <div key={r.t} className="flex items-center justify-between border-b border-slate-200 pb-2.5">
              <div>
                <div className="text-[14px] font-semibold text-slate-900">{r.t}</div>
                <div className="text-[11px] text-slate-500">{r.note}</div>
              </div>
              <div className="font-display text-xl font-bold text-slate-900">{r.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Includes */}
      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 font-bold mb-3">Incluye</div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
          {[
            "Plataforma SIGSAOP en producción",
            "Soporte técnico 24/7",
            "Backups automáticos diarios",
            "Cifrado de extremo a extremo",
            "Acceso multi-usuario con roles",
            "Actualizaciones y mejoras continuas",
            "Acompañamiento de implementación",
            "Capacitación inicial (2 sesiones)",
          ].map((t) => (
            <div key={t} className="flex items-center gap-2 text-[13px] text-slate-700">
              <CheckCircle size={16} weight="duotone" className="text-brand-green-600 flex-shrink-0" />
              {t}
            </div>
          ))}
        </div>
      </div>
    </PageFrame>
  );
}

// =================================================================
// 09 — Plan Copilot (PPE 2)
// =================================================================
function PagePlanCopilot() {
  return (
    <PageFrame id="plan-copilot" title="Plan Copilot · Propuesta Económica" num="09" total="11">
      <SectionLabel accent="green">Plan Copilot · Recomendado</SectionLabel>
      <h2 className="font-display text-[44px] leading-[1.05] font-bold mt-4 text-slate-900 max-w-[700px]">
        Plan <span className="text-brand-green-600">Copilot</span>.
      </h2>
      <p className="text-[15px] text-slate-600 mt-4 leading-relaxed max-w-[700px]">
        Todo lo del Plan O&S, más una ejecutiva SIGSAOP dedicada a cargar y mantener la
        información operacional medio día, lunes a viernes.
      </p>

      {/* Pricing card */}
      <div className="mt-6 rounded-2xl border-2 border-brand-green-500/50 bg-gradient-to-br from-brand-green-50 to-white p-6 shadow-lg">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-green-700 font-bold">Inversión mensual</div>
            <div className="font-display text-[56px] font-bold text-slate-900 leading-none mt-2">
              $2.300.000
            </div>
            <div className="text-[12px] text-slate-500 mt-2">CLP mensual · no incluye IVA</div>
          </div>
          <div className="text-right">
            <CurrencyDollar size={56} weight="duotone" className="text-brand-green-600" />
          </div>
        </div>

        <div className="mt-6 space-y-2.5">
          {[
            { t: "Diseño y personalización", v: "$0", note: "Sin costo · incluido" },
            { t: "Operación y Soporte Técnico 24/7", v: "$1.500.000", note: "Mensual" },
            { t: "Onboarding de datos (ejecutiva dedicada)", v: "$800.000", note: "L-V · medio día · horario administrativo" },
          ].map((r) => (
            <div key={r.t} className="flex items-center justify-between border-b border-slate-200 pb-2.5">
              <div>
                <div className="text-[14px] font-semibold text-slate-900">{r.t}</div>
                <div className="text-[11px] text-slate-500">{r.note}</div>
              </div>
              <div className="font-display text-xl font-bold text-slate-900">{r.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Includes - copilot specific */}
      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 font-bold mb-3">Incluye todo lo del Plan O&S, más</div>
        <div className="grid grid-cols-1 gap-y-2">
          {[
            "Ejecutiva dedicada para carga y mantención de información",
            "Servicio de lunes a viernes · medio día · horario administrativo",
            "Tiempo de carga: rango de 12 horas hábiles según volumen",
            "Validación final del usuario creador o designado por FCAB",
          ].map((t) => (
            <div key={t} className="flex items-center gap-2 text-[13px] text-slate-700">
              <CheckCircle size={16} weight="duotone" className="text-brand-green-600 flex-shrink-0" />
              {t}
            </div>
          ))}
        </div>
      </div>
    </PageFrame>
  );
}

// =================================================================
// 10 — Términos y Condiciones
// =================================================================
function PageTerminos() {
  return (
    <PageFrame id="terminos" title="Términos técnicos y comerciales" num="10" total="11">
      <SectionLabel>Términos técnicos y comerciales</SectionLabel>
      <h2 className="font-display text-[36px] leading-[1.05] font-bold mt-4 text-slate-900 max-w-[700px]">
        Detalles de la Propuesta Técnica.
      </h2>

      <div className="grid grid-cols-2 gap-x-5 gap-y-4 mt-6">
        {[
          { icon: Wrench, t: "Diseño customizable", d: "Campos, módulos, ejes y permisos a medida de FCAB. Sin costo en cambios contemplados por el sistema." },
          { icon: GraduationCap, t: "Acompañamiento integral", d: "Implementación, capacitación y soporte continuo durante todo el ciclo." },
          { icon: Lightning, t: "Soporte técnico 24/7", d: "Monitoreo continuo de la plataforma." },
          { icon: LockKey, t: "Seguridad de datos", d: "Cifrado de extremo a extremo, backups 24/7 en AWS." },
          { icon: ShieldCheck, t: "Ley 21.719 de Chile", d: "Alineado con la nueva Ley de Protección de Datos Personales de Chile." },
          { icon: ClipboardText, t: "Confidencialidad", d: "Aceptamos cláusulas de confidencialidad y compliance que tenga la compañía." },
          { icon: Calendar, t: "Servicio Copilot", d: "Carga de datos lunes a viernes en horario administrativo, medio día." },
          { icon: Database, t: "Tiempos de carga", d: "Rango de 12 horas hábiles dependiendo del volumen de información entregada." },
          { icon: CheckCircle, t: "Validación final", d: "Requiere aprobación del usuario creador o designado por el mandante FCAB." },
          { icon: Receipt, t: "Condiciones comerciales", d: "Valores en CLP, no incluyen IVA. Vigencia de la propuesta: 30 días desde su emisión." },
        ].map((it) => (
          <div key={it.t} className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
              <it.icon size={18} weight="duotone" className="text-brand-orange-600" />
            </div>
            <div>
              <div className="font-display text-[14px] font-bold text-slate-900 leading-tight">{it.t}</div>
              <div className="text-[12px] text-slate-600 mt-1 leading-snug">{it.d}</div>
            </div>
          </div>
        ))}
      </div>
    </PageFrame>
  );
}

// =================================================================
// 11 — Cierre
// =================================================================
function PageCierre() {
  return (
    <Page id="cierre" bg="orange">
      <div className="absolute -top-32 -right-20 w-[500px] h-[500px] rounded-full bg-white/20 blur-3xl" />
      <div className="absolute -bottom-40 -left-32 w-[600px] h-[600px] rounded-full bg-amber-300/30 blur-3xl" />

      <div className="relative h-full flex flex-col justify-between px-14 py-14">
        <div>
          <SectionLabel accent="white">Cierre · Próximos pasos</SectionLabel>
          <h2 className="font-display text-[68px] leading-[0.95] font-bold mt-6 tracking-tight">
            Listos para<br />acompañar a <span className="underline decoration-white/40 decoration-[6px] underline-offset-8">FCAB</span>.
          </h2>
          <p className="text-[18px] text-white/95 mt-8 leading-snug max-w-[660px]">
            Esta propuesta consolida la Propuesta Técnica (PPT) y la Propuesta Económica (PPE).
            Quedamos disponibles para resolver dudas o alcances y dar inicio al cronograma de 14 días.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-4">
            <div className="rounded-2xl border-2 border-white/30 bg-white/10 backdrop-blur-sm p-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/80 font-bold">Plan O&S</div>
              <div className="font-display text-3xl font-bold mt-2">$1.500.000</div>
              <div className="text-[12px] text-white/85 mt-1">CLP mensual · sin IVA</div>
            </div>
            <div className="rounded-2xl border-2 border-white bg-white/15 backdrop-blur-sm p-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white font-bold">Plan Copilot · Recomendado</div>
              <div className="font-display text-3xl font-bold mt-2">$2.300.000</div>
              <div className="text-[12px] text-white/85 mt-1">CLP mensual · sin IVA</div>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-end justify-between">
            <img
              src="/assets/sigsaop-logo-light.svg"
              alt="SIGSAOP"
              className="h-24 w-auto"
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/80">Contacto</div>
              <div className="font-display text-2xl font-bold mt-1">sigsaop.cl</div>
            </div>
          </div>
          <div className="mt-5 pt-5 border-t border-white/20 flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-white/70 font-semibold">
            <span>Antofagasta · Chile</span>
            <span>Documento confidencial · Junio 2026</span>
          </div>
        </div>
      </div>
    </Page>
  );
}

// =================================================================
// Main page
// =================================================================
const PAGES = [
  { Component: PageCover, title: "01 · Portada" },
  { Component: PageResumen, title: "02 · Resumen Ejecutivo" },
  { Component: PageProblema, title: "03 · El Problema" },
  { Component: PageMetodologia, title: "04 · Metodología" },
  { Component: PageEntregables, title: "05 · Entregables" },
  { Component: PageValor, title: "06 · Valor Diferencial" },
  { Component: PageCronograma, title: "07 · Cronograma" },
  { Component: PagePlanOS, title: "08 · Plan O&S" },
  { Component: PagePlanCopilot, title: "09 · Plan Copilot" },
  { Component: PageTerminos, title: "10 · Términos" },
  { Component: PageCierre, title: "11 · Cierre" },
];

export default function PpFcab() {
  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 print-page">
      <header className="bg-white border-b border-slate-200 shadow-sm print:hidden">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-bold">Propuesta SIGSAOP · FCAB</h1>
            <p className="text-sm text-slate-500">PPT + PPE · 11 páginas A4 · listo para guardar como PDF</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.print()}
              className="rounded-full bg-brand-orange-500 hover:bg-brand-orange-600 text-white font-bold px-5 py-2.5 text-sm transition shadow-lg shadow-brand-orange-500/30"
              data-testid="pp-fcab-print-btn"
            >
              Imprimir / Guardar PDF
            </button>
            <Link
              to="/brochures"
              className="text-sm text-slate-600 hover:text-slate-900 transition"
            >
              ← Brochures
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-[900px] mx-auto px-6 py-10 space-y-10 print:space-y-0 print:p-0 print:max-w-none">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 print:hidden">
          <strong className="text-slate-900 font-bold">Documento propuesta — uso interno SIGSAOP</strong>
          <p className="mt-2">11 páginas A4 portrait con resumen ejecutivo, propuesta técnica y propuesta económica para FCAB. Usa el botón <em>Imprimir / Guardar PDF</em> para descargar como archivo.</p>
        </div>

        {PAGES.map(({ Component, title }, i) => (
          <section key={title} className="print:break-after-page print:m-0">
            <div className="flex items-center justify-between mb-3 print:hidden">
              <h3 className="font-display text-base font-bold text-slate-800">{title}</h3>
              <span className="text-xs uppercase tracking-wider text-slate-400">{i + 1} / {PAGES.length}</span>
            </div>
            <div className="flex justify-center">
              <div
                className="pp-page-scaler"
                style={{ width: PAGE_W * 0.85, height: PAGE_H * 0.85, overflow: "hidden" }}
                data-testid={`pp-page-${i + 1}`}
              >
                <div className="pp-page-inner" style={{ transform: "scale(0.85)", transformOrigin: "top left" }}>
                  <Component />
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 0; }
          body, html { background: white !important; margin: 0; padding: 0; }
          .print-page { background: white !important; }
          .pp-page-scaler {
            width: auto !important;
            height: auto !important;
            overflow: visible !important;
          }
          .pp-page-inner {
            transform: none !important;
          }
        }
        .font-display { font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif; }
      `}</style>
    </div>
  );
}
